import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatHours, TOOLTIP_CURSOR, TOOLTIP_STYLE, toKey, toNumber } from './chart-theme';
import { useChartAnimation } from './use-chart-animation';

/** What Recharts passes a custom axis tick renderer. */
interface AxisTickProps {
  x?: string | number;
  y?: string | number;
  payload: { value: unknown };
}

export interface StackedSeries {
  key: string;
  label: string;
  color: string;
}

export interface StackedRow {
  /** Row label on the left axis. */
  name: string;
  /** Smaller second line under the label (e.g. "India · ACT · 118 ppl"). */
  caption?: string;
  /** Value shown on the right edge — the row's headline figure. */
  total: string;
  /** One entry per series key. */
  values: Record<string, number>;
}

/**
 * Horizontal stacked bars, one row per group, with a labelled left axis and a
 * headline total on the right.
 *
 * Horizontal because the row labels are words of varying length — as vertical
 * bars they'd have to be rotated or truncated. Rows are clickable so the whole
 * bar acts as the drilldown affordance, not just a small hit target.
 */
export function StackedCategoryBar({
  rows,
  series,
  max,
  onRowClick,
}: {
  rows: StackedRow[];
  series: StackedSeries[];
  /** Upper bound of the value axis — pass the largest capacity so rows are comparable. */
  max: number;
  onRowClick?: (index: number) => void;
}) {
  const animation = useChartAnimation();
  const data = rows.map((r) => ({ name: r.name, ...r.values }));
  const byName = new Map(rows.map((r) => [r.name, r]));
  const labelFor = new Map(series.map((s) => [s.key, s.label]));

  // Two-line category tick: the group's name over its caption.
  const NameTick = ({ x, y, payload }: AxisTickProps) => {
    const row = byName.get(String(payload.value));
    return (
      <g transform={`translate(${Number(x)},${Number(y)})`}>
        <text
          x={-8}
          y={-2}
          textAnchor="end"
          fontSize="12.5"
          fontWeight={600}
          fill="var(--foreground)"
        >
          {String(payload.value)}
        </text>
        <text x={-8} y={12} textAnchor="end" fontSize="10.5" fill="var(--muted-foreground)">
          {row?.caption}
        </text>
      </g>
    );
  };

  // Right-hand axis is used purely to park the row's headline figure at a
  // fixed x, so the numbers line up in a column instead of floating at the
  // end of each bar.
  const TotalTick = ({ x, y, payload }: AxisTickProps) => (
    <text
      x={Number(x) + 4}
      y={Number(y) + 4}
      textAnchor="start"
      fontSize="13"
      fontWeight={700}
      fill="var(--foreground)"
    >
      {byName.get(String(payload.value))?.total}
    </text>
  );

  return (
    <div style={{ width: '100%', height: rows.length * 46 + 12 }}>
      <ResponsiveContainer>
        <BarChart
          layout="vertical"
          data={data}
          barCategoryGap={10}
          margin={{ top: 0, right: 74, bottom: 0, left: 0 }}
        >
          <XAxis type="number" hide domain={[0, max]} />
          <YAxis
            yAxisId="name"
            type="category"
            dataKey="name"
            width={150}
            tickLine={false}
            axisLine={false}
            tick={NameTick}
          />
          <YAxis
            yAxisId="total"
            orientation="right"
            type="category"
            dataKey="name"
            width={70}
            tickLine={false}
            axisLine={false}
            tick={TotalTick}
          />
          <Tooltip
            cursor={TOOLTIP_CURSOR}
            contentStyle={TOOLTIP_STYLE}
            formatter={(value, key) => [
              formatHours(toNumber(value)),
              labelFor.get(toKey(key)) ?? toKey(key),
            ]}
          />
          {series.map((s, i) => (
            <Bar
              key={s.key}
              yAxisId="name"
              dataKey={s.key}
              stackId="a"
              fill={s.color}
              // Round only the outer ends so the stack reads as one bar.
              radius={i === 0 ? [4, 0, 0, 4] : i === series.length - 1 ? [0, 4, 4, 0] : 0}
              cursor={onRowClick ? 'pointer' : undefined}
              onClick={(_, index) => onRowClick?.(index)}
              {...animation}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
